import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchRepSafeJson } from '../../../lib/apiFetchJson';
import { validateResponse } from '../../../lib/validateResponse';
import { FlowPlaysResponseSchema, type FlowPlaysResponse } from '@shared/flow/plays-view';
import type { FilterGroup, GroupLogic } from '@shared/flow/filter';
import { FLOW_PLAYS_QUERY_KEY } from './keys';

/**
 * `useFlowPlays` — `GET /api/flow/plays` (plan
 * `plans/2026-09-09-003-terra-signals-plays-tab-plan.md` U4, D9; the
 * `filters` contract per `plans/2026-09-10-001-plays-tab-filter-redesign-plan.md`
 * amendment A2, unit U9). Same shape as `useFlowContacts`
 * (`contact/hooks.ts`): every param change requeries (no separate refetch
 * trigger needed), `staleTime: Infinity` + `placeholderData: keepPreviousData`
 * per the project's cache-indefinitely / invalidate-on-mutation rule (D14 —
 * `EnrollQueueContext.tsx` invalidates `FLOW_PLAYS_QUERY_KEY`, imported from
 * `./keys`, not from this module).
 *
 * A2: `scope`/`q`/`type` are GONE. The Plays list's own filters are now the
 * same `FilterGroup[]` contract the Contact component uses, compiled
 * server-side over `PLAYS_FIELD_MAP` — "Playhouse visible only" is just the
 * default seeded chip (`CHIP_PLAYHOUSE_VISIBLE`), the play-type tabs are a
 * `play_type` condition. Nothing about what the list is showing is hidden
 * from the rep any more, so nothing here needs a param the popover can't
 * produce. (The search box was also a `play_name contains` condition under
 * A2/A7; A17 split it back out — see below.)
 *
 * A17: `search` is a REQUEST PARAM, not a `play_name` filter chip. As a chip
 * it was working state, so each committed keystroke marked the view dirty and
 * was persisted into the `plays` view-state row — a rep's throwaway search
 * came back on reload as a saved filter. It is part of the query key, so
 * typing requeries rather than serving the previous value from the
 * `staleTime: Infinity` cache.
 *
 * ABORT SIGNAL (Codex round-1 #6's second half, round-2 #6 — now DONE):
 * React Query's `signal` is handed to `apiFetch`, so a request superseded by
 * the next keystroke is actually cancelled instead of racing to completion
 * and costing three Terra-Signals count aggregates server-side. This was
 * previously declined for a real reason — `apiFetch` did
 * `signal: fetchInit.signal || controller.signal`, so passing a signal
 * REPLACED the per-call 15 s timeout, trading one leak for another. `lib/api.ts`
 * now COMPOSES the two via `AbortSignal.any`, which is what makes threading
 * it safe. The 300 ms debounce in `PlaysSubtab` still stands as the first
 * line of defence; cancellation covers what slips past it.
 */
export interface UseFlowPlaysParams {
  /** The rep's visible, removable chips, serialized to `?filters=<JSON>`; `[]` means "every play". */
  filters: FilterGroup[];
  /**
   * A17 — the toolbar Search box's text, serialized to `?search=<value>`.
   * Transient: held in plain React state by `PlaysSubtab`, never a
   * `FilterGroup`, never persisted in view-state, never marks the working
   * state dirty. Empty / whitespace-only omits the param entirely.
   */
  search?: string;
  /**
   * A20 — the OUTER combinator between `filters` groups, i.e. the AND/OR
   * switch `FilterPopover` renders. Omitted from the URL for the `'AND'`
   * default (the route defaults to AND), sent explicitly for `'OR'`.
   *
   * Part of the query key like `filters`, so flipping the switch requeries
   * instead of serving the AND result out of the `staleTime: Infinity` cache
   * — which would reproduce Codex round-2 #1's exact symptom (pick OR, see
   * nothing change) with a correct server.
   */
  groupLogic?: GroupLogic;
}

export interface UseFlowPlaysOptions {
  /**
   * Gate the request (default `true`) — a disabled query, not a query for
   * nothing. (Its original caller, `useScopedPlayCount`, was deleted with
   * the play-scope wiring in plan 2026-09-24-005 U15/D11.)
   */
  enabled?: boolean;
}

export function useFlowPlays(params: UseFlowPlaysParams, options: UseFlowPlaysOptions = {}) {
  const { apiFetch } = useApi();
  const { enabled = true } = options;

  return useQuery<FlowPlaysResponse>({
    // The groups are part of the KEY (by value — react-query hashes the
    // object), so editing a chip requeries rather than serving the previous
    // filter set from the `staleTime: Infinity` cache.
    queryKey: [...FLOW_PLAYS_QUERY_KEY, params],
    enabled,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    // Codex #6 — React Query's own signal, composed with `apiFetch`'s
    // timeout inside `lib/api.ts`, so a superseded request is cancelled.
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams();
      // Omitted (not `filters=[]`) when the rep cleared every chip: the
      // route's own default for an absent param is the same empty array, and
      // an omitted key keeps the URL readable in the network tab.
      if (params.filters.length > 0) qs.set('filters', JSON.stringify(params.filters));
      // A17 — omitted (not `search=`) when the box is empty, for the same
      // readability reason, and trimmed here so the key `''` and `'   '`
      // both mean "no search" on the client too. The server trims again
      // (`activeSearch`), which is the authority.
      const search = params.search?.trim() ?? '';
      if (search !== '') qs.set('search', search);
      if (params.groupLogic === 'OR') qs.set('group_logic', 'OR');

      // U13 (finding 4-5) — `fetchRepSafeJson` covers the rejected-fetch and
      // bad-JSON paths this used to leave raw, and (per this plan) deletes
      // the old `Failed to load plays (${res.status})` status-code leak.
      const body = await fetchRepSafeJson(apiFetch, `/api/flow/plays?${qs.toString()}`, {
        signal,
        fallback: "Couldn't load plays right now.",
      });
      return validateResponse(FlowPlaysResponseSchema, body, 'GET /api/flow/plays');
    },
  });
}
